import PropTypes from 'prop-types';
import React from 'react';
import { Field, Fields, formValueSelector } from 'redux-form/immutable';
import { useSelector } from 'react-redux';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Tooltip,
  FormControlLabel,
} from '@material-ui/core';
import DeleteIcon from '@mui/icons-material/Delete';

import { Features } from 'utils/features';
import FormDateWithStartAndEnd from 'retired/FormDateWithStartAndEnd';
import FormTextField from 'retired/FormTextField';
import FormRadioButton from 'retired/FormRadioButton';
import * as validators from 'utils/validators';
import { currencies } from 'utils/currencyFormat';
import DialogButton from 'retired/DialogButton';
import FormIncludeExcludeSelector from 'retired/FormIncludeExcludeSelector';
import FormLocationSelector from 'retired/FormLocationSelector';
import FormCheckbox from 'retired/FormCheckbox';
import { TotalLimitLabel } from 'containers/DonationRulesListPage/TotalLimitLabel';
import { selectUser } from 'entities/user/selectors';
import { GivingAccountSelector } from 'components/GivingAccountSelector/GivingAccountSelector';

import { FORM_IDENTIFIER } from './constants';
import { CapGroups } from './CapGroups';

const required = validators.required();

const valueSelector = formValueSelector(FORM_IDENTIFIER);

const CurrencyCodeSelector = ({ input }) => (
  <Select value={input.value} onChange={input.onChange}>
    {Object.keys(currencies).map((currencyCode) => {
      const currency = currencies[currencyCode];
      return (
        <MenuItem key={currencyCode} value={currencyCode}>
          {currency.label}
        </MenuItem>
      );
    })}
  </Select>
);
CurrencyCodeSelector.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.string,
    onChange: PropTypes.func,
  }),
};

const GivingAccountSelectorWrapper = ({ children }) => (
  <div style={{ marginBottom: 20 }}>
    <Grid container spacing={3}>
      <Grid item md={12}>
        <FormControl style={{ width: '100%', height: 17 }}>
          <InputLabel shrink>Giving Account</InputLabel>
        </FormControl>
        {children}
      </Grid>
    </Grid>
  </div>
);
GivingAccountSelectorWrapper.propTypes = {
  children: PropTypes.node,
};

const Budget = ({ pristine, invalid, remove, budgetId, hasRules, locations }) => {
  const user = useSelector(selectUser);
  const currencyCode = useSelector((state) => valueSelector(state, 'currencyCode'));
  const limitAssertionType = useSelector((state) => valueSelector(state, 'limitAssertionType'));

  const selectedCurrency = currencies[currencyCode || 'USD'];

  const validateLocations = (value, _budget) => {
    if (_budget && !_budget.get('locationsExclude') && !(value?.size || value?.length)) {
      return 'The budget is set to only include certain locations and none are selected';
    }
    return undefined;
  };

  const validateTotalLimit = (value, _budget) => {
    if (!value) {
      return undefined;
    }
    const valueAsNumber = Number(value);
    if (valueAsNumber === 0) {
      return 'Total limit cannot equal 0';
    }
    if (_budget && Number(_budget.get('limitPerUser')) > valueAsNumber) {
      return 'Total limit can not be less than limit per user';
    }
    if (_budget && Number(_budget.get('totalIssued')) > valueAsNumber) {
      return 'You can not set the limit to less than what has already been spent';
    }
    return undefined;
  };
  return (
    <div
      style={{
        border: '1px solid rgba(0, 0, 0, 0.2)',
        padding: 30,
        marginBottom: 30,
        maxWidth: 900,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <div style={{ flexGrow: 1 }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Budget Name</InputLabel>
          </FormControl>
          <FormControl style={{ width: '100%' }}>
            <Field
              name="title"
              component={FormTextField}
              placeholder="Budget Name"
              validate={required}
              required
            />
          </FormControl>
        </div>
        {budgetId !== 'new' && !hasRules && (
          <DialogButton
            style={{ display: 'inline-block' }}
            title="Remove Budget"
            text="Are you sure you want to remove this Giving Budget?"
            confirmLabel="Remove"
            cancelLabel="Cancel"
            iconButton
            handleConfirm={remove}
          >
            <DeleteIcon />
          </DialogButton>
        )}
      </div>
      <div style={{ marginBottom: 20 }}>
        <Grid container spacing={3}>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Budget Currency</InputLabel>
            </FormControl>
            <Field
              name="currencyCode"
              component={CurrencyCodeSelector}
              validate={required}
              required
              type="string"
            />
          </Grid>
          <Grid item md={8}>
            <div style={{ marginTop: 17 }}>
              <Field
                name="limitToCurrency"
                component={FormCheckbox}
                label="Limit to donations in budget currency"
              />
            </div>
          </Grid>
        </Grid>
      </div>

      <Field
        name="givingAccount"
        component={GivingAccountSelector}
        type="string"
        Wrapper={GivingAccountSelectorWrapper}
      />

      <div style={{ marginBottom: 30 }}>
        <FormControl style={{ width: '100%', height: 17 }}>
          <InputLabel shrink>
            Choose start and end dates when this budget should be active or leave empty
          </InputLabel>
        </FormControl>

        <div>
          <Fields
            names={[`startingAt`, `endingAt`]}
            component={FormDateWithStartAndEnd}
            displayTimeAndTimeZone
            future
          />
        </div>
      </div>
      <div style={{ marginBottom: 30 }}>
        <FormIncludeExcludeSelector name="locationsExclude" labelText="the following locations:" />
        <Field
          name="locations"
          component={FormLocationSelector}
          props={{ locations }}
          validate={validateLocations}
        />
      </div>

      <div style={{ marginBottom: 30 }}>
        <Grid container spacing={3}>
          <Grid item md>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Limit Per User</InputLabel>
            </FormControl>
            {selectedCurrency.symbol}
            <Field
              name="limitPerUser"
              component={FormTextField}
              placeholder="Limit Per User"
              type="number"
              alwaysDisplayErrors
            />

            {(user?.hasFeature(Features.BUDGET_LIMIT_ASSERTION_TYPE) ||
              limitAssertionType === 'Contribution') && (
              <div style={{ marginTop: 10 }}>
                <Field name="limitAssertionType" component={FormRadioButton}>
                  <RadioGroup row>
                    <Tooltip title="Enforced based on their rewards (matched amount)">
                      <FormControlLabel
                        control={<Radio />}
                        value="Reward"
                        label="Reward"
                        checked={limitAssertionType === 'Reward'}
                        disabled={budgetId !== 'new'}
                      />
                    </Tooltip>
                    <Tooltip title="Enforced based on their contributions (donated amount)">
                      <FormControlLabel
                        control={<Radio />}
                        value="Contribution"
                        label="Contribution"
                        checked={limitAssertionType === 'Contribution'}
                        disabled={budgetId !== 'new'}
                      />
                    </Tooltip>
                  </RadioGroup>
                </Field>
              </div>
            )}
          </Grid>
          <Grid item md>
            <TotalLimitLabel />
            {selectedCurrency.symbol}
            <Field
              name="limitTotal"
              component={FormTextField}
              placeholder="Total Limit"
              validate={validateTotalLimit}
              type="number"
              alwaysDisplayErrors
            />
          </Grid>
        </Grid>
      </div>

      <div>
        <CapGroups currencySymbol={selectedCurrency?.symbol} />
      </div>

      <div style={{ marginTop: 30 }}>
        <Button type="submit" disabled={invalid} color="primary" size="large" variant="contained">
          {pristine ? 'Close' : 'Submit'}
        </Button>
      </div>
    </div>
  );
};
Budget.propTypes = {
  locations: PropTypes.object,
  pristine: PropTypes.bool,
  invalid: PropTypes.bool,
  remove: PropTypes.func,
  budgetId: PropTypes.string,
  hasRules: PropTypes.bool,
};

export default Budget;
